import { useEffect, useMemo, useState } from 'react';
import type { PublicSnapshotResponse } from '@volt/shared';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { createApiClient } from '../api/client.js';
import { useI18n } from '../i18n/context.js';
import { copyToClipboard } from '../lib/dom.js';
import { extractShareToken } from '../lib/share.js';
import { Icon } from './Icon.js';

type SnapshotMessage = PublicSnapshotResponse['snapshot']['messages'][number];

function CopyButton({ text }: { text: string }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    const ok = await copyToClipboard(text);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  }

  const label = copied ? t('message.copied') : t('message.copy');

  return (
    <button
      type="button"
      className="shared-copy-btn"
      aria-label={label}
      title={label}
      onClick={() => void handleCopy()}
    >
      <Icon name={copied ? 'check' : 'copy'} size={14} />
    </button>
  );
}

function SharedMessage({ message }: { message: SnapshotMessage }) {
  const { t } = useI18n();
  const isUser = message.role === 'user';

  if (isUser) {
    return (
      <div className="shared-message shared-message-user">
        {message.content && (
          <>
            <div className="shared-bubble-user">
              <div className="shared-bubble-text">{message.content}</div>
            </div>
            <div className="shared-message-actions">
              <CopyButton text={message.content} />
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="shared-message shared-message-assistant">
      <div className="shared-assistant-label">{t('common.assistant')}</div>
      <div className="shared-assistant-content">
        <Markdown remarkPlugins={[remarkGfm]}>{message.content}</Markdown>
      </div>
      {message.content && (
        <div className="shared-message-actions">
          <CopyButton text={message.content} />
        </div>
      )}
    </div>
  );
}

export function SharedPage() {
  const { t } = useI18n();
  const api = useMemo(() => createApiClient(), []);
  const token = useMemo(() => extractShareToken(window.location.pathname), []);
  const [snapshot, setSnapshot] = useState<PublicSnapshotResponse['snapshot'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setNotFound(true);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setSnapshot(null);
    void api
      .getPublicSnapshot(token)
      .then((response) => {
        if (!cancelled) setSnapshot(response.snapshot);
      })
      .catch(() => {
        if (!cancelled) {
          setSnapshot(null);
          setNotFound(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, token]);

  return (
    <div className="shared-page">
      <header className="shared-header">
        <div className="shared-header-meta">
          {snapshot && (
            <>
              <span className="shared-header-title">{snapshot.title || t('share.title')}</span>
              <span className="shared-header-date">
                {new Date(snapshot.createdAt).toLocaleDateString(undefined, {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })}
              </span>
            </>
          )}
        </div>
        <a href="/" className="btn btn-primary shared-open-btn" style={{ textDecoration: 'none' }}>
          {t('share.openApp')}
        </a>
      </header>

      <main className="shared-main">
        {loading ? (
          <p className="shared-status">{t('share.loading')}</p>
        ) : notFound || !snapshot ? (
          <p className="shared-status">{t('share.notFound')}</p>
        ) : (
          <div className="shared-feed">
            {snapshot.messages.map((message) => (
              <SharedMessage key={message.id} message={message} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
